import React, { useState, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination"
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem
} from "@/components/ui/dropdown-menu";
import { Download, Search, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, RefreshCw } from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { exportSecureCSV, type CsvExportConfig } from "@/utils/csvSecurity"

export interface DataTableColumn<T> {
  key: keyof T
  header: React.ReactNode
  sortable?: boolean
  searchable?: boolean
  className?: string
  render?: (value: unknown, row: T) => React.ReactNode
  exportRender?: (value: unknown, row: T) => string | number
  hidden?: boolean // For column visibility
  hideFromDropdown?: boolean // Hide from column visibility dropdown
}

export interface DataTableConfig {
  searchPlaceholder?: string
  defaultSortField?: string
  defaultSortDirection?: 'asc' | 'desc'
  rowsPerPage?: number
  showSearch?: boolean
  showExport?: boolean
  showPagination?: boolean
  showColumnVisibility?: boolean
  showRefresh?: boolean
  exportFilename?: string
  emptyStateMessage?: string
  noSearchResultsMessage?: string
}

interface DataTableProps<T> {
  data: T[]
  columns: DataTableColumn<T>[]
  title?: string
  isLoading?: boolean
  className?: string
  config?: DataTableConfig
  onRefresh?: () => void | Promise<void>
  toolbarChildren?: React.ReactNode
  rowStyle?: (row: T) => React.CSSProperties
}

type SortDirection = 'asc' | 'desc'

export function DataTable<T extends Record<string, unknown>>({
  data,
  columns,
  title,
  isLoading = false,
  className,
  config = {},
  onRefresh,
  toolbarChildren,
  rowStyle
}: DataTableProps<T>) {
  const {
    searchPlaceholder = "Search...",
    defaultSortField = '',
    defaultSortDirection = 'asc',
    rowsPerPage = 10,
    showSearch = true,
    showExport = true,
    showPagination = true,
    showColumnVisibility = true,
    showRefresh = false,
    exportFilename = 'data',
    emptyStateMessage = "No data available",
    noSearchResultsMessage = "No results found matching your search"
  } = config

  const [searchQuery, setSearchQuery] = useState<string>("")
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [sortField, setSortField] = useState<string>(defaultSortField)
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection)
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    // Initialize column visibility based on hidden property
    const initialVisibility: Record<string, boolean> = {}
    columns.forEach(col => {
      initialVisibility[String(col.key)] = !col.hidden
    })
    return initialVisibility
  })

  // Get searchable columns
  const searchableColumns = columns.filter(col => col.searchable !== false)

  // Get visible columns
  const visibleColumns = columns.filter(col => columnVisibility[String(col.key)] !== false)

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
    setCurrentPage(1) // Reset to first page when sorting
  }

  const getSortIcon = (field: string) => {
    const column = columns.find(col => col.key === field)
    if (!column?.sortable) return null
    
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-2 text-muted-foreground" />
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-2 text-primary" />
      : <ArrowDown className="h-4 w-4 ml-2 text-primary" />
  }

  // Handle refresh
  const handleRefresh = async () => {
    if (!onRefresh) return
    
    try {
      await toast.promise(
        Promise.resolve(onRefresh()),
        {
          loading: 'Refreshing data...',
          success: 'Data refreshed successfully',
          error: 'Failed to refresh data',
        }
      );
    } catch (error) {
      console.error('Refresh failed:', error)
    }
  }

  // Filter data based on search query with memoized intermediate results
  const filteredData = useMemo(() => {
    let filtered = data;
    // Apply search filter
    if (searchQuery.trim() && searchableColumns.length > 0) {
      const query = searchQuery.toLowerCase().trim();
      const queryTerms = query.split(/\s+/).filter(term => term.length > 0);
      // Only filter if we have valid search terms
      if (queryTerms.length > 0) {
        filtered = data.filter(row => 
          searchableColumns.some(col => {
            const value = String(row[col.key] || '').toLowerCase();
            // Normalize value to remove diacritics for accent-insensitive search
            const normalizedValue = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
            return queryTerms.every(term => normalizedValue.includes(term));
          })
        );
      }
    }
    return filtered;
  }, [data, searchQuery, searchableColumns]);

  // Apply sorting to filtered data
  const filteredAndSortedData = useMemo(() => {
    const result = [...filteredData];
    // Apply sorting
    if (sortField) {
      result.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];
        // Handle different data types
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
          aValue = String(aValue || '').toLowerCase();
          bValue = String(bValue || '').toLowerCase();
          if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }
      });
    }
    return result;
  }, [filteredData, sortField, sortDirection]);

  // Calculate pagination with memoized page numbers to prevent recalculation
  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(filteredAndSortedData.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedData = filteredAndSortedData.slice(startIndex, endIndex);
    
    // Pre-calculate page numbers to show
    const pageNumbers = [];
    if (totalPages <= 7) {
      // If 7 or fewer pages, show all
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Show first, last, current, and adjacent pages
      const pagesToShow = new Set([1, totalPages]);
      for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
        pagesToShow.add(i);
      }
      pageNumbers.push(...Array.from(pagesToShow).sort((a, b) => a - b));
    }
    
    return {
      totalPages,
      startIndex,
      endIndex,
      paginatedData,
      pageNumbers
    };
  }, [filteredAndSortedData, rowsPerPage, currentPage]);

  // Use the memoized pagination data
  const { totalPages, startIndex, endIndex, paginatedData, pageNumbers } = paginationData;

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  // Handle CSV export with security
  const handleExport = async () => {
    if (!filteredAndSortedData.length) {
      toast.error("No data to export")
      return
    }

    try {
      const headers = visibleColumns
        .map(col => (typeof col.header === 'string' ? col.header : String(col.key)))
      const csvData = filteredAndSortedData.map(row => 
        visibleColumns.map(col => {
          const value = row[col.key]
          if (col.exportRender) {
            return col.exportRender(value, row)
          }
          return value
        })
      )

      const config: CsvExportConfig = {
        filename: exportFilename,
        headers,
        preventInjection: true
      }

      await exportSecureCSV(csvData, config)
      toast.success("Data exported successfully")
    } catch (error) {
      console.error('CSV export failed:', error)
      toast.error("Failed to export data")
    }
  }

  if (isLoading) {
    return (
      <Card className={cn("w-full", className)}>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-muted-foreground">Loading data...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={cn("w-full", className)}>
      {(title || showSearch || showExport || showColumnVisibility || showRefresh || toolbarChildren) && (
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            {title && <CardTitle className="text-lg font-semibold">{title}</CardTitle>}
            {(showSearch || showExport || showColumnVisibility || showRefresh || toolbarChildren) && (
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto items-center">
                {showSearch && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={searchPlaceholder}
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value)
                        setCurrentPage(1) // Reset to first page when searching
                      }}
                      className="pl-10 w-full sm:w-80"
                    />
                  </div>
                )}
                {toolbarChildren}
                <div className="flex items-center gap-2">
                  {showRefresh && onRefresh && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefresh}
                      className="whitespace-nowrap"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                  )}
                  {showExport && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExport}
                      disabled={!filteredAndSortedData.length}
                      className="whitespace-nowrap"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  )}
                  {showColumnVisibility && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          Columns <ChevronDown className="ml-2 h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                         {columns.filter(column => !column.hideFromDropdown).map((column) => {
                          return (
                            <DropdownMenuCheckboxItem
                              key={String(column.key)}
                              className="capitalize"
                              checked={columnVisibility[String(column.key)] !== false}
                              onCheckedChange={(value) =>
                                setColumnVisibility(prev => ({
                                  ...prev,
                                  [String(column.key)]: !!value
                                }))
                              }
                            >
                               {typeof column.header === 'string' ? column.header : String(column.key)}
                            </DropdownMenuCheckboxItem>
                          )
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
      )}
      
      <CardContent className="p-0">
        {filteredAndSortedData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-muted-foreground mb-2">
              {searchQuery ? noSearchResultsMessage : emptyStateMessage}
            </div>
            {searchQuery && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setSearchQuery("")
                  setCurrentPage(1)
                }}
              >
                Clear search
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="relative w-full overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 border-b">
                    {visibleColumns.map((column) => (
                      <TableHead 
                        key={String(column.key)}
                        className={cn(
                          "text-center font-semibold text-sm py-3 h-auto border-0",
                          column.sortable !== false ? "cursor-pointer hover:bg-muted/70 select-none transition-colors" : "",
                          column.className
                        )}
                        onClick={() => column.sortable !== false ? handleSort(String(column.key)) : undefined}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span className="font-semibold">{column.header}</span>
                          {getSortIcon(String(column.key))}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedData.map((row, index) => (
                    <TableRow 
                      key={index} 
                      className="hover:bg-muted/50 transition-colors h-16 border-b border-border"
                      style={rowStyle ? rowStyle(row) : undefined}
                    >
                      {visibleColumns.map((column) => (
                        <TableCell 
                          key={String(column.key)} 
                          className={cn(
                            "text-center font-medium py-4 border-0 px-3",
                            column.className
                          )}
                        >
                          {column.render 
                            ? column.render(row[column.key], row)
                            : String(row[column.key] || '')
                          }
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {showPagination && totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <div className="text-sm text-muted-foreground whitespace-nowrap text-left">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredAndSortedData.length)} of {filteredAndSortedData.length} entries
                  {searchQuery && ` (filtered from ${data.length} total)`}
                </div>
                <div className="ml-auto">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => handlePageChange(currentPage - 1)}
                          className={cn(
                            "cursor-pointer",
                            currentPage === 1 && "pointer-events-none opacity-50"
                          )}
                        />
                      </PaginationItem>
                      {/* Page numbers */}
                      {pageNumbers.map((page, index, array) => {
                        // Add ellipsis if there's a gap
                        const showEllipsis = index > 0 && page > array[index - 1] + 1
                        return (
                          <PaginationItem key={page}>
                            {showEllipsis && (
                              <span className="px-3 py-2 text-muted-foreground">...</span>
                            )}
                            <PaginationLink
                              onClick={() => handlePageChange(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        )
                      })}
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => handlePageChange(currentPage + 1)}
                          className={cn(
                            "cursor-pointer",
                            currentPage === totalPages && "pointer-events-none opacity-50"
                          )}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default DataTable 